import type { PreloadedQuery } from 'react-relay';
import CONNECTED_TO_ISSUE_AGG_DATA_QUERY, {
	type ConnectedToIssueAggQuery,
} from '@jira/issue__connected-to-issue-agg-query/src/__generated__/ConnectedToIssueAggQuery.graphql';
import { isWorkItemModernizationAllowedForProject } from '@jira/issue__eligibility/src/isWorkItemModernizationAllowedForProject.tsx';
import { getIssueKey } from '@jira/router-resources__utils/src/getIssueKey.tsx';
// eslint-disable-next-line jira/import-whitelist
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { JiraSiteAri } from '@atlassian/ari/jira/site';

import type { RouteResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';

export const connectedToIssueAggDataResource: RouteResource<
	PreloadedQuery<ConnectedToIssueAggQuery, Record<string, unknown>>
> = createRelayResource<ConnectedToIssueAggQuery>({
	type: 'CONNECTED_TO_ISSUE_AGG_DATA',
	getQuery: ({ match, query }, { tenantContext }) => {
		const issueKey = getIssueKey(match) || query?.selectedIssue || '';
		if (!issueKey || tenantContext?.isAnonymous) {
			return {
				parameters: CONNECTED_TO_ISSUE_AGG_DATA_QUERY,
				variables: {
					cloudId: '',
					issueKey: '',
					siteAri: '',
					isAnonymous: !!tenantContext?.isAnonymous,
					includeDesignPanelData: false,
					includeWhiteboardsCountInAttachmentConsolidation: false,
					includeReleasesPanelDevOpsData: false,
					workItemModernizationEnabled: false,
					includeIncidentCommandCenterData: false,
					isJsmPreventionCenterEnabled: false,
					includeAgentSessionsInConnectedTo: false,
				},
				options: { fetchPolicy: 'store-only' },
			};
		}
		const cloudId = tenantContext?.cloudId || '';
		const siteAri = JiraSiteAri.create({ siteId: cloudId }).toString();
		const workItemModernizationEnabled =
			(isWorkItemModernizationAllowedForProject(cloudId, issueKey) &&
				// eslint-disable-next-line jira/ff/no-work-item-modernization-exp-val
				expVal('work_item_modernization', 'isEnabled', false)) ||
			expVal('work_item_for_quadratic', 'isEnabled', false);
		return {
			parameters: CONNECTED_TO_ISSUE_AGG_DATA_QUERY,
			variables: {
				cloudId: tenantContext?.cloudId || '',
				issueKey,
				siteAri,
				isAnonymous: false,
				consistentRead: fg('design-panel-ags-consistent-read'),
				includeDesignPanelData: true,
				includeWhiteboardsCountInAttachmentConsolidation: expVal(
					'attachments-1p-consolidation-experiment',
					'isExperimentEnabled',
					false,
				),
				includeReleasesPanelDevOpsData: fg('jpx-1073-remove-jswddfetchoperation'),
				workItemModernizationEnabled,
				includeIncidentCommandCenterData:
					expVal('measure_performance_impact_of_icc_feature_toggle', 'isEnabled', false) ||
					fg('jsm_incident_command_center'),
				isJsmPreventionCenterEnabled: fg('jsm_prevention_center'),
				includeAgentSessionsInConnectedTo: fg('jira_agent_sessions_connected_to_read'),
			},
			options: { fetchPolicy: 'store-or-network' },
		};
	},
	isBrowserOnly: false,
});
